/**
 * Identity Authentication Hook
 * Simplified React hook for Identity-based authentication
 * Alternative to using the full context provider
 */

import { useState, useEffect } from 'react';
import { identityAuth } from './IdentityAuth';

export function useIdentityAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [claims, setClaims] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const auth = await identityAuth.checkAuth();
      setIsAuthenticated(auth);
      
      if (auth) {
        const tokenClaims = identityAuth.getClaims();
        const user = identityAuth.getUserInfo();
        
        setClaims(tokenClaims);
        setUserInfo(user);
        
        // Start periodic session checks if authenticated
        identityAuth.startSessionCheck();
      } else {
        setUserInfo(null);
      }
      
      setLoading(false);
    };

    checkAuth();

    // Cleanup on unmount
    return () => {
      identityAuth.stopSessionCheck();
    };
  }, []);

  const logout = async () => {
    setLoading(true);
    await identityAuth.logout();
  };

  const fetchWithAuth = (url, options) => {
    return identityAuth.fetchWithAuth(url, options);
  };

  const getToken = () => {
    return identityAuth.getToken();
  };

  const getClaims = () => {
    return identityAuth.getClaims();
  };

  const hasRole = (role) => {
    return identityAuth.hasRole(role);
  };

  const getUserInfo = () => {
    return identityAuth.getUserInfo();
  };

  return {
    isAuthenticated,
    claims,
    userInfo,
    loading,
    logout,
    fetchWithAuth,
    getToken,
    getClaims,
    hasRole,
    getUserInfo,
  };
}
